import { useCallback, useMemo } from "react";

import type { FilterType } from "@tsmono/inspect-components/columnFilter";
import { Icon, IconName } from "@tsmono/react/icons";
import { basename, formatNumber, formatPrettyDecimal } from "@tsmono/util";

import { useLogDir } from "../../../../app_config";
import { scopePrefix } from "../../../../client/database";
import { kModelNone } from "../../../../constants";
import {
  useLogListing,
  useScoreSchema,
  type LogListingRow,
  type ScorerMap,
} from "../../../../log_data";
import { useStore } from "../../../../state/store";
import { parseLogFileName } from "../../../../utils/evallog";
import { formatDateTime, formatTime } from "../../../../utils/format";
import {
  ColumnComparator,
  ExtendedColumnDef,
} from "../../../shared/data-grid/columnTypes";
import sharedStyles from "../../../shared/gridCells.module.css";
import { type PickerColumn } from "../../../shared/gridUtils";

import localStyles from "./columns.module.css";
import { dateCompare, numberCompare } from "./comparators";
import { completedAtValue } from "./completedAt";
import { LogListRow } from "./types";

const styles = { ...sharedStyles, ...localStyles };

type LogListColumn = ExtendedColumnDef<LogListRow>;

const EmptyCell = () => <div>-</div>;

const kNoScorerMap: ScorerMap = {};
const kNoListingRows: LogListingRow[] = [];

const displayModelRoles = (row: LogListRow | undefined): [string, string][] => {
  if (!row) return [];
  if (row.model && row.model !== kModelNone) return [];
  return row.modelRoles ? Object.entries(row.modelRoles) : [];
};

const primaryModelValue = (row: LogListRow | undefined): string | undefined => {
  if (!row) return undefined;
  if (row.model && row.model !== kModelNone) return row.model;
  return displayModelRoles(row)[0]?.[1];
};

/** Human-readable header: "scorer / metric". */
const scorerMetricHeader = (scorerName: string, metricName: string): string =>
  `${scorerName} / ${metricName}`;

/**
 * Field key for the synthetic "by metric" column that aggregates across
 * scorers. Uses a `metric_` prefix (no slash) so it can never collide with
 * the per-scorer `score_<scorer>/<metric>` field keys.
 */
const byMetricField = (metricName: string): string => `metric_${metricName}`;

export type LogListMode = "logs" | "tasks";
export type ScoresViewMode = "by-metric" | "per-scorer";

export const useLogListColumns = (
  mode: LogListMode = "logs",
  /**
   * When set, scorer columns are computed only from logs under this
   * directory. Used by the folder view so descending into a subfolder
   * recomputes the Metrics list to match the contents of that folder.
   */
  scopeDir?: string,
  /**
   * View mode for scorer columns:
   *   - "by-metric" (default): one synthetic column per unique metric name,
   *     aggregating across scorers via accessorFn.
   *   - "per-scorer": one column per (scorer, metric) pair, fully qualified.
   */
  viewMode: ScoresViewMode = "by-metric"
): {
  /** Full column set for the grid. Both score-column modes are registered;
   *  visibility is applied by the DataGrid via its `columnVisibility` state
   *  so the column-def reference stays stable across visibility toggles. */
  columns: LogListColumn[];
  /** Visibility map keyed by column id — passed to the DataGrid. */
  visibility: Record<string, boolean>;
  /** Subset passed to the ColumnSelectorPopover so the picker only lists
   *  checkboxes for the currently active view mode. A lightweight shim
   *  (`colId` + `headerName`) of the full column defs. */
  pickerColumns: PickerColumn[];
  /** Reads a row's raw value for a column id (for client-side filter/sort). */
  getValue: (row: LogListRow, columnId: string) => unknown;
  /** Per-column value comparator (from column meta) for client-side sort. */
  getComparator: (columnId: string) => ColumnComparator | undefined;
  /** Per-column filter type (from column meta) for client-side filtering. */
  getFilterType: (columnId: string) => FilterType | undefined;
  /** Cache identity of the accessors above: the scorer schema they're built
   *  from. The schema arrives asynchronously, so a query that closes over
   *  the accessors must carry this in its key — score-column semantics
   *  (by-metric accessors, numeric comparators/filter types) change when it
   *  lands, with no other query input changing. */
  accessorsKey: string;
  setColumnVisibility: (visibility: Record<string, boolean>) => void;
} => {
  const columnVisibility = useStore(
    (state) => state.logs.listing.columnVisibility
  );
  const setColumnVisibility = useStore(
    (state) => state.logsActions.setLogsColumnVisibility
  );
  const logDir = useLogDir();
  // Settled schema only: column defs are decorative config — while the
  // listing loads there are simply no scorer columns yet, and listing errors
  // render in LogsPanel's error surface.
  const scorerMap = useScoreSchema(logDir, scopeDir).data ?? kNoScorerMap;

  const allColumns = useMemo((): LogListColumn[] => {
    const baseColumns: LogListColumn[] = [
      {
        id: "type",
        header: "",
        size: 32,
        minSize: 32,
        maxSize: 32,
        enableResizing: false,
        meta: { align: "center" },
        accessorFn: (row) => row.type,
        cell: ({ row }) => {
          const type = row.original.type;
          const icon: IconName =
            type === "file" || type === "pending-task"
              ? "inspectFile"
              : "folder";
          return (
            <div className={styles.iconCell}>
              <Icon name={icon} />
            </div>
          );
        },
      },
      {
        id: "task",
        header: "Task",
        size: 250,
        minSize: 150,
        accessorFn: (row) => {
          if (row.type === "file") {
            return row.task || parseLogFileName(row.name).name;
          }
          return row.name;
        },
        titleValue: (row) => {
          if (row.type === "file") {
            return row.task || parseLogFileName(row.name).name;
          }
          return row.name;
        },
        cell: ({ row }) => {
          const item = row.original;
          let value = item.name;
          if (item.type === "file") {
            value = item.task || parseLogFileName(item.name).name;
          }
          return (
            <div className={styles.nameCell}>
              {item.type === "folder" ? (
                <span className={styles.folder}>{value}</span>
              ) : (
                <span className={styles.taskText}>{value}</span>
              )}
            </div>
          );
        },
      },
      {
        id: "model",
        header: "Model",
        size: 300,
        minSize: 100,
        maxSize: 400,
        accessorFn: (row) => primaryModelValue(row),
        titleValue: (row) => {
          const roles = displayModelRoles(row);
          if (roles.length > 0) {
            return roles.map(([role, model]) => `${role}: ${model}`).join("\n");
          }
          const model = row.model;
          return model && model !== kModelNone ? model : undefined;
        },
        cell: ({ row }) => {
          const item = row.original;
          if (item.model && item.model !== kModelNone) {
            return <div className={styles.modelCell}>{item.model}</div>;
          }
          const roles = displayModelRoles(item);
          const firstRole = roles[0];
          if (firstRole) {
            const primary = firstRole[1];
            const extras = roles.length - 1;
            return (
              <div className={styles.modelCell}>
                <span className={styles.modelCellPrimary}>{primary}</span>
                {extras > 0 && (
                  <span className={styles.multiScorerBadge}>+{extras}</span>
                )}
              </div>
            );
          }
          return <EmptyCell />;
        },
      },
      {
        id: "score",
        header: "Score",
        size: 80,
        minSize: 60,
        maxSize: 120,
        meta: { sortComparator: numberCompare },
        accessorFn: (row) => row.score,
        textValue: (row) =>
          row.score === undefined ? null : formatPrettyDecimal(row.score),
        cell: ({ row }) => {
          const item = row.original;
          if (item.score === undefined) {
            return <EmptyCell />;
          }
          return (
            <div className={styles.scoreCell}>
              {formatPrettyDecimal(item.score)}
            </div>
          );
        },
      },
      {
        id: "status",
        header: "Status",
        size: 80,
        minSize: 60,
        maxSize: 100,
        accessorFn: (row) => row.status,
        titleValue: (row) => {
          if (row.status === "error" && row.errorMessage) {
            return row.errorMessage;
          }
          return row.status || undefined;
        },
        cell: ({ row }) => {
          const item = row.original;
          const status = item.status;

          if (!status && item.type !== "pending-task") {
            return <EmptyCell />;
          }

          const icon: IconName =
            item.type === "pending-task"
              ? "pendingTaskSubtle"
              : status === "error"
                ? "error"
                : status === "started"
                  ? "running"
                  : status === "cancelled"
                    ? "cancel"
                    : "success";

          const clz =
            item.type === "pending-task"
              ? styles.started
              : status === "error"
                ? styles.error
                : status === "started"
                  ? styles.started
                  : status === "cancelled"
                    ? styles.cancelled
                    : styles.success;

          return (
            <div className={styles.statusCell}>
              <Icon name={icon} className={clz} />
            </div>
          );
        },
      },
      {
        id: "completedAt",
        header: "Completed",
        size: 130,
        minSize: 80,
        maxSize: 140,
        meta: { sortComparator: dateCompare },
        // Raw value for sort/filter; the cell formats from row.original.
        accessorFn: (row) => completedAtValue(row),
        cell: ({ row }) => {
          const completed = row.original.completedAt;
          if (!completed) {
            return <EmptyCell />;
          }
          return (
            <div className={styles.dateCell}>
              {formatDateTime(new Date(completed))}
            </div>
          );
        },
      },
      {
        id: "name",
        header: "File Name",
        size: 600,
        minSize: 150,
        accessorFn: (row) => {
          if (row.type === "folder") return row.name;
          if (row.type === "file") return basename(row.name);
          return "";
        },
        titleValue: (row) => {
          if (row.type === "folder") return row.name;
          if (row.type === "file") return basename(row.name);
          return undefined;
        },
        cell: ({ row }) => {
          const item = row.original;
          if (item.type === "pending-task") {
            return <EmptyCell />;
          }
          if (item.type === "folder") {
            return (
              <div className={styles.nameCell}>
                <span className={styles.folder}>{item.name}</span>
              </div>
            );
          }
          return <div className={styles.nameCell}>{basename(item.name)}</div>;
        },
      },
      {
        id: "path",
        header: "Path",
        size: 300,
        minSize: 100,
        accessorFn: (row) => row.path,
        titleValue: (row) => row.path,
        cell: ({ row }) => {
          const item = row.original;
          if (!item.path) return <EmptyCell />;
          return <div className={styles.nameCell}>{item.path}</div>;
        },
      },
      {
        id: "totalSamples",
        header: "Samples",
        size: 90,
        minSize: 60,
        maxSize: 120,
        meta: { sortComparator: numberCompare },
        accessorFn: (row) => row.totalSamples,
        cell: ({ getValue }) => {
          const value = getValue<number | undefined>();
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- getValue's type argument is an unchecked assertion; row data may contain nulls the type omits
          if (value === undefined || value === null) {
            return <EmptyCell />;
          }
          return <div>{formatNumber(value)}</div>;
        },
      },
      {
        id: "completedSamples",
        header: "Completed Samples",
        size: 130,
        minSize: 80,
        maxSize: 160,
        meta: { sortComparator: numberCompare },
        accessorFn: (row) => row.completedSamples,
        cell: ({ getValue }) => {
          const value = getValue<number | undefined>();
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- getValue's type argument is an unchecked assertion; row data may contain nulls the type omits
          if (value === undefined || value === null) {
            return <EmptyCell />;
          }
          return <div>{formatNumber(value)}</div>;
        },
      },
      {
        id: "sandbox",
        header: "Sandbox",
        size: 100,
        minSize: 60,
        maxSize: 150,
        accessorFn: (row) => row.sandbox,
        cell: ({ getValue }) => {
          const value = getValue<string | undefined>();
          if (!value) return <EmptyCell />;
          return <div>{value}</div>;
        },
      },
      {
        id: "totalTokens",
        header: "Tokens",
        size: 100,
        minSize: 60,
        maxSize: 140,
        meta: { sortComparator: numberCompare },
        accessorFn: (row) => row.totalTokens,
        cell: ({ getValue }) => {
          const value = getValue<number | undefined>();
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- getValue's type argument is an unchecked assertion; row data may contain nulls the type omits
          if (value === undefined || value === null) {
            return <EmptyCell />;
          }
          return <div>{formatNumber(value)}</div>;
        },
      },
      {
        id: "duration",
        header: "Duration",
        size: 120,
        minSize: 70,
        maxSize: 160,
        meta: { sortComparator: numberCompare },
        accessorFn: (row) => row.duration,
        titleValue: (row) =>
          row.duration === undefined ? undefined : formatTime(row.duration),
        textValue: (row) =>
          row.duration === undefined ? null : formatTime(row.duration),
        cell: ({ getValue }) => {
          const value = getValue<number | undefined>();
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- getValue's type argument is an unchecked assertion; row data may contain nulls the type omits
          if (value === undefined || value === null) {
            return <EmptyCell />;
          }
          return <div>{formatTime(value)}</div>;
        },
      },
      {
        id: "taskFile",
        header: "Task File",
        size: 200,
        minSize: 100,
        accessorFn: (row) => row.taskFile,
        titleValue: (row) => row.taskFile,
        cell: ({ getValue }) => {
          const value = getValue<string | undefined>();
          if (!value) return <EmptyCell />;
          return <div className={styles.nameCell}>{value}</div>;
        },
      },
      {
        id: "taskArgs",
        header: "Task Args",
        size: 200,
        minSize: 100,
        accessorFn: (row) => row.taskArgs,
        titleValue: (row) =>
          row.taskArgsRaw
            ? JSON.stringify(row.taskArgsRaw, null, 2)
            : undefined,
        cell: ({ getValue }) => {
          const value = getValue<string | undefined>();
          if (!value) return <EmptyCell />;
          return <div className={styles.nameCell}>{value}</div>;
        },
      },
      {
        id: "tags",
        header: "Tags",
        size: 80,
        minSize: 80,
        accessorFn: (row) =>
          row.tags && row.tags.length > 0 ? row.tags.join(", ") : "",
        titleValue: (row) =>
          row.tags && row.tags.length > 0 ? row.tags.join(", ") : undefined,
        cell: ({ getValue }) => {
          const value = getValue<string>();
          if (!value) return <EmptyCell />;
          return <div className={styles.nameCell}>{value}</div>;
        },
      },
      {
        id: "percentCompleted",
        header: "% Completed",
        size: 110,
        minSize: 80,
        maxSize: 140,
        meta: { sortComparator: numberCompare },
        accessorFn: (row) => row.percentCompleted,
        textValue: (row) =>
          row.percentCompleted === undefined
            ? null
            : `${formatPrettyDecimal(row.percentCompleted)}%`,
        cell: ({ getValue }) => {
          const value = getValue<number | undefined>();
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- getValue's type argument is an unchecked assertion; row data may contain nulls the type omits
          if (value === undefined || value === null) {
            return <EmptyCell />;
          }
          return <div>{formatPrettyDecimal(value)}%</div>;
        },
      },
      {
        id: "sampleErrors",
        header: "Sample Errors",
        size: 110,
        minSize: 60,
        maxSize: 140,
        meta: { sortComparator: numberCompare },
        accessorFn: (row) => row.sampleErrors,
        cell: ({ getValue }) => {
          const value = getValue<number | undefined>();
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- getValue's type argument is an unchecked assertion; row data may contain nulls the type omits
          if (value === undefined || value === null) {
            return <EmptyCell />;
          }
          return <div>{formatNumber(value)}</div>;
        },
      },
      {
        id: "sampleLimits",
        header: "Sample Limits",
        size: 140,
        minSize: 80,
        accessorFn: (row) => row.sampleLimits,
        cell: ({ getValue }) => {
          const value = getValue<string | undefined>();
          if (!value) return <EmptyCell />;
          return <div>{value}</div>;
        },
      },
      {
        id: "errorMessage",
        header: "Error",
        size: 300,
        minSize: 100,
        accessorFn: (row) =>
          row.errorMessage ? row.errorMessage.split("\n")[0] : "",
        titleValue: (row) => row.errorMessage || undefined,
        cell: ({ getValue }) => {
          const value = getValue<string>();
          if (!value) return <EmptyCell />;
          return <div className={styles.nameCell}>{value}</div>;
        },
      },
    ];

    // Per-scorer columns: one per (scorer, metric) pair. Alphabetical key
    // order so the column sequence is stable regardless of log iteration.
    const perScorerColumns: LogListColumn[] = Object.entries(scorerMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, { scorerName, metricName, valueType }]) => {
        return {
          id: `score_${key}`,
          header: scorerMetricHeader(scorerName, metricName),
          size: 100,
          minSize: 100,
          meta:
            valueType === "number"
              ? { sortComparator: numberCompare }
              : undefined,
          accessorFn: (row) => row[`score_${key}`],
          textValue: (row) => {
            const value = row[`score_${key}`];
            if (typeof value === "number") return formatPrettyDecimal(value);
            if (typeof value === "boolean") return String(value);
            return typeof value === "string" && value !== "" ? value : null;
          },
          cell: ({ getValue }) => {
            const value = getValue<
              string | number | boolean | null | undefined
            >();
            if (value === undefined || value === null || value === "") {
              return <EmptyCell />;
            }
            return (
              <div className={styles.scoreCell}>
                {typeof value === "number"
                  ? formatPrettyDecimal(value)
                  : String(value)}
              </div>
            );
          },
        };
      });

    // By-metric columns: one synthetic column per unique metric name across
    // all scorers. Each column's accessorFn reads the row's per-scorer fields
    // in alphabetical scorer order and returns the first non-empty value.
    // The cell additionally renders a "+N" badge with a tooltip when more
    // than one scorer on the same row produced the metric.
    const metricGroups = new Map<
      string,
      { scorerName: string; valueType: string }[]
    >();
    for (const { scorerName, metricName, valueType } of Object.values(
      scorerMap
    )) {
      const list = metricGroups.get(metricName) ?? [];
      list.push({ scorerName, valueType });
      metricGroups.set(metricName, list);
    }

    const byMetricColumns: LogListColumn[] = [...metricGroups.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([metricName, entries]) => {
        const scorerOrder = entries
          .map((e) => e.scorerName)
          .sort((a, b) => a.localeCompare(b));
        const allNumeric = entries.every((e) => e.valueType === "number");

        const readContributors = (
          row: LogListRow | undefined
        ): { scorer: string; value: string | number | boolean }[] => {
          if (!row) return [];
          const contributors: {
            scorer: string;
            value: string | number | boolean;
          }[] = [];
          for (const scorer of scorerOrder) {
            const v = row[`score_${scorer}/${metricName}`];
            if (
              typeof v === "string" ||
              typeof v === "number" ||
              typeof v === "boolean"
            ) {
              if (v !== "") contributors.push({ scorer, value: v });
            }
          }
          return contributors;
        };

        return {
          id: byMetricField(metricName),
          header: metricName,
          size: 100,
          minSize: 100,
          meta: allNumeric ? { sortComparator: numberCompare } : undefined,
          accessorFn: (row) => readContributors(row)[0]?.value,
          textValue: (row) => {
            const first = readContributors(row)[0];
            if (!first) return null;
            return typeof first.value === "number"
              ? formatPrettyDecimal(first.value)
              : String(first.value);
          },
          cell: ({ row }) => {
            const [first, ...extras] = readContributors(row.original);
            if (!first) return <EmptyCell />;
            const primary = first.value;
            const primaryText =
              typeof primary === "number"
                ? formatPrettyDecimal(primary)
                : String(primary);
            return (
              <div className={styles.scoreCell}>
                {primaryText}
                {extras.length > 0 && (
                  <span
                    className={styles.multiScorerBadge}
                    title={extras
                      .map((c) => {
                        const v =
                          typeof c.value === "number"
                            ? formatPrettyDecimal(c.value)
                            : String(c.value);
                        return `${c.scorer}: ${v}`;
                      })
                      .join("\n")}
                  >
                    +{extras.length}
                  </span>
                )}
              </div>
            );
          },
        };
      });

    // Always include BOTH score-column sets so the grid's column structure
    // is stable across view-mode toggles — switching just flips visibility
    // on each column rather than swapping in an entirely new column array.
    const allCols = [...baseColumns, ...perScorerColumns, ...byMetricColumns];

    if (mode === "tasks") {
      // Tasks view: remove the type icon column (no folders in flat view)
      const typeIdx = allCols.findIndex((col) => col.id === "type");
      if (typeIdx >= 0) {
        allCols.splice(typeIdx, 1);
      }

      // Tasks view column order
      const tasksFieldOrder = [
        "status",
        "task",
        "model",
        "taskArgs",
        "tags",
        "score",
        "completedAt",
        "totalSamples",
        "completedSamples",
        "percentCompleted",
        "sampleErrors",
        "sampleLimits",
        "totalTokens",
        "duration",
        "errorMessage",
        "name",
        "sandbox",
        "taskFile",
      ];
      allCols.sort((a, b) => {
        const aIdx = tasksFieldOrder.indexOf(a.id || "");
        const bIdx = tasksFieldOrder.indexOf(b.id || "");
        // Fields in the order list come first, in specified order.
        // Fields not in the list (scorer columns) go after.
        const aOrder = aIdx >= 0 ? aIdx : tasksFieldOrder.length;
        const bOrder = bIdx >= 0 ? bIdx : tasksFieldOrder.length;
        return aOrder - bOrder;
      });
    } else {
      // Logs view: move "name" (File Name) to be the second column (after
      // the type icon)
      const nameIdx = allCols.findIndex((col) => col.id === "name");
      if (nameIdx > 1) {
        const [nameCol] = allCols.splice(nameIdx, 1);
        if (nameCol) allCols.splice(1, 0, nameCol);
      }

      // move "status" to be right after the name column
      const statusIdx = allCols.findIndex((col) => col.id === "status");
      if (statusIdx > 2) {
        const [statusCol] = allCols.splice(statusIdx, 1);
        if (statusCol) allCols.splice(2, 0, statusCol);
      }
    }

    // Every column except the type icon is filterable (matches origin/main's
    // `defaultColDef.filter: true` with the type column opted out). Derive the
    // filter type from the sort comparator: numeric / date columns get their
    // typed editors; everything else filters as text.
    for (const col of allCols) {
      if (col.id === "type") continue;
      const cmp = col.meta?.sortComparator;
      const filterType: FilterType =
        cmp === numberCompare
          ? "number"
          : cmp === dateCompare
            ? "date"
            : "string";
      col.meta = { ...col.meta, filterable: true, filterType };
    }

    return allCols;
  }, [scorerMap, mode]);

  // Auto-promote `sampleLimits` to default-visible when any in-scope log
  // has a sample that ended with a limit (an ingestion-derived header fact).
  const listingRows = useLogListing(logDir).data ?? kNoListingRows;
  const hasSampleLimits = useMemo(() => {
    const prefix = scopeDir ? scopePrefix(scopeDir) : undefined;
    return listingRows.some(
      (row) =>
        (!prefix || row.name.startsWith(prefix)) &&
        (row.header?.sampleLimits.length ?? 0) > 0
    );
  }, [listingRows, scopeDir]);

  // Default hidden columns per mode
  const defaultHiddenFields = useMemo(() => {
    const hidden = new Set<string>();
    if (mode === "tasks") {
      // Tasks: hide completedSamples, sandbox, taskFile by default
      hidden.add("sandbox");
      hidden.add("taskFile");
      hidden.add("name");
      hidden.add("path");
      hidden.add("completedSamples");
    } else {
      // Logs: hide path, completedSamples, sandbox, taskFile by default
      hidden.add("path");
      hidden.add("completedSamples");
      hidden.add("sandbox");
      hidden.add("taskFile");
    }
    // New columns default to hidden in both views
    hidden.add("percentCompleted");
    hidden.add("sampleErrors");
    if (!hasSampleLimits) hidden.add("sampleLimits");
    hidden.add("errorMessage");
    return hidden;
  }, [mode, hasSampleLimits]);

  // Determine whether a column belongs to the active scores view mode. Base
  // columns (neither prefix) always match. Per-scorer and by-metric columns
  // only match when the corresponding mode is selected.
  const matchesActiveMode = (field: string): boolean => {
    if (field.startsWith("score_")) return viewMode === "per-scorer";
    if (field.startsWith("metric_")) return viewMode === "by-metric";
    return true;
  };

  // Visibility map keyed by column id, consumed by the DataGrid.
  const visibility = useMemo<Record<string, boolean>>(() => {
    const v: Record<string, boolean> = {};
    for (const col of allColumns) {
      const field = col.id;
      if (field === undefined) continue;
      const isScoreColumn =
        field.startsWith("score_") || field.startsWith("metric_");
      const defaultVisible = isScoreColumn
        ? false
        : !defaultHiddenFields.has(field);
      v[field] = columnVisibility[field] ?? defaultVisible;
    }
    return v;
  }, [allColumns, columnVisibility, defaultHiddenFields]);

  // Columns to show in the ColumnSelectorPopover. The grid needs both score
  // column sets registered for layout stability, but the picker should only
  // list the checkboxes relevant to the current view mode.
  const pickerColumns = useMemo((): PickerColumn[] => {
    return allColumns.flatMap((col) =>
      col.id === undefined || !matchesActiveMode(col.id)
        ? []
        : [
            {
              colId: col.id,
              headerName: typeof col.header === "string" ? col.header : "",
            },
          ]
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- matchesActiveMode is recreated each render but is safe to exclude
  }, [allColumns, viewMode]);

  // Lookup by column id for the client-side listing query's value/comparator
  // accessors.
  const columnsById = useMemo(() => {
    const byId = new Map<string, LogListColumn>();
    for (const col of allColumns) {
      if (col.id) byId.set(col.id, col);
    }
    return byId;
  }, [allColumns]);

  const getValue = useCallback(
    (row: LogListRow, columnId: string): unknown => {
      const col = columnsById.get(columnId);
      if (col && "accessorFn" in col && typeof col.accessorFn === "function") {
        return col.accessorFn(row, 0);
      }
      return row[columnId];
    },
    [columnsById]
  );

  const getComparator = useCallback(
    (columnId: string): ColumnComparator | undefined =>
      columnsById.get(columnId)?.meta?.sortComparator,
    [columnsById]
  );

  const getFilterType = useCallback(
    (columnId: string): FilterType | undefined =>
      columnsById.get(columnId)?.meta?.filterType,
    [columnsById]
  );

  // Everything the accessors read beyond the row and the column id: the
  // scorer schema (`mode` also shapes the column set, but only in ways the
  // accessors don't observe — and it's part of the listing universe anyway).
  const accessorsKey = useMemo(
    () =>
      Object.entries(scorerMap)
        .map(([key, { valueType }]) => `${key}:${valueType}`)
        .sort()
        .join(","),
    [scorerMap]
  );

  return {
    columns: allColumns,
    visibility,
    pickerColumns,
    getValue,
    getComparator,
    getFilterType,
    accessorsKey,
    setColumnVisibility,
  };
};
